/**
 * The 24-month example (TMMRSL-0049, 0052): tmm-api's preview of one subscriber on the picked rate
 * card, paying its monthly base from their first payment — each payment's date and rate, the
 * affiliate's commission, the reseller's margin before and after it, and the day each commission
 * becomes payable (DATA-008, RPT-001: the basis states the price, the start date and the cool-off;
 * TBL-014: a totals row). The price is the same every month, so it is in the basis, not a column.
 * The console computes nothing but the totals of the server's rows. From TMMAPI-0105 (TMMRSL-0051)
 * a Bounty column follows Commission when any month carries `bountyMinor`, a capped month says so in
 * text after its commission (STATE-002), and when the plan has milestone bonuses a caption states
 * they aren't in this one-subscriber example (DATA-008). The last example stays on
 * screen at full contrast in a `BusyRegion` (progress bar, `aria-busy`) while the next one loads
 * (PERF-001). The rate cards failing affects only this section (PERF-007).
 */
import {
  Alert,
  Box,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { GrantedCard } from '@/api/rateCards';
import { ApiErrorState } from '@/components/ApiErrorState';
import { BusyRegion } from '@/components/BusyRegion';
import { MoneyCell, NumericCell } from '@/components/cells';
import { moneyIn } from '@/components/moneyIn';
import type { RemoteState } from '@/components/useRemote';
import { formatCivilDate } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { exampleTotals, exampleView } from './exampleState';
import type { PlanPreviewState } from './usePlanPreview';
import { FIELD_WIDTH } from '@/theme';

export interface PlanExampleProps {
  state: PlanPreviewState;
  cards: RemoteState<GrantedCard[]>;
  /** The cards the example can use, in order (`exampleCardChoices`). */
  choices: GrantedCard[];
  /** The picked card (`?card=`, or the first choice). */
  card: GrantedCard | null;
  onPickCard: (id: number) => void;
  /** The form has milestone rows: say they aren't in the example. */
  hasMilestones?: boolean;
}

export function PlanExample({
  state,
  cards,
  choices,
  card,
  onPickCard,
  hasMilestones = false,
}: PlanExampleProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const dash = t('statements.unavailable');
  const view = exampleView(state, cards, choices);
  const busy = view.kind === 'ready' && view.busy;
  const title = t('commissionPlans.example.title');

  function body() {
    switch (view.kind) {
      case 'cardsError':
        return <ApiErrorState error={view.error} onRetry={cards.reload} />;
      case 'cardsLoading':
      case 'calculating':
        return (
          <Typography role="status" variant="body2" sx={{ color: 'text.secondary' }}>
            {t('commissionPlans.example.calculating')}
          </Typography>
        );
      case 'noCard':
        return (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('commissionPlans.example.noCard')}
          </Typography>
        );
      case 'failed':
        return <Alert severity="error">{t('commissionPlans.example.failed')}</Alert>;
      case 'cardGone':
        // The page reloads the cards; the picker then falls back to its default.
        return <Alert severity="warning">{t('commissionPlans.example.cardGone')}</Alert>;
      case 'idle':
      case 'invalid':
        // Idle: a client error blocks the body, so nothing is loading. Invalid: tmm-api sent no
        // example because the schedule itself is invalid; the marked fields say why (UI-010).
        return (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('commissionPlans.example.fixFields')}
          </Typography>
        );
      case 'ready':
        break;
    }
    const { preview, example } = view;
    const money = (minor: number | null) =>
      minor == null ? dash : moneyIn(minor, example.currency, locale, dash);
    const totals = exampleTotals(example);
    const basisCard = choices.find((choice) => choice.id === example.rateCardId) ?? card;
    const first = example.months[0];
    return (
      <BusyRegion busy={busy}>
        {first && basisCard ? (
          <Typography variant="caption" component="p" sx={{ color: 'text.secondary', mb: 1.5 }}>
            {t('commissionPlans.example.basis', {
              card: basisCard.name,
              price: money(example.retailMinor),
              date: formatCivilDate(first.collectedOn, locale),
              count: example.coolOffDays,
            })}
          </Typography>
        ) : null}
        {preview.valid ? null : (
          <Alert severity="warning" variant="outlined" sx={{ mb: 1.5 }}>
            {t('commissionPlans.example.outOfLimits')}
          </Alert>
        )}
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label={title}>
            <TableHead>
              <TableRow>
                <TableCell>{t('commissionPlans.example.monthCol')}</TableCell>
                <TableCell>{t('commissionPlans.example.collected')}</TableCell>
                <TableCell align="right">{t('commissionPlans.example.rate')}</TableCell>
                <TableCell align="right">{t('commissionPlans.example.commission')}</TableCell>
                {example.hasBounty ? (
                  <TableCell align="right">{t('commissionPlans.example.bounty')}</TableCell>
                ) : null}
                <TableCell align="right">{t('commissionPlans.example.margin')}</TableCell>
                <TableCell align="right">{t('commissionPlans.example.after')}</TableCell>
                <TableCell>{t('commissionPlans.example.payable')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {example.months.map((month) => (
                <TableRow key={month.month}>
                  <TableCell component="th" scope="row" sx={{ whiteSpace: 'nowrap' }}>
                    {t('commissionPlans.example.month', { n: month.month })}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {formatCivilDate(month.collectedOn, locale)}
                  </TableCell>
                  <NumericCell value={month.rateBp} percentBp />
                  <MoneyCell minor={month.commissionMinor} currency={example.currency}>
                    {month.capped ? (
                      <Typography
                        variant="caption"
                        component="span"
                        sx={{ display: 'block', color: 'text.secondary' }}
                      >
                        {t('commissionPlans.example.capped')}
                      </Typography>
                    ) : null}
                  </MoneyCell>
                  {example.hasBounty ? (
                    <MoneyCell minor={month.bountyMinor} currency={example.currency} />
                  ) : null}
                  <MoneyCell minor={month.marginBeforeMinor} currency={example.currency} />
                  <MoneyCell minor={month.marginAfterMinor} currency={example.currency} />
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {month.payableOn !== null &&
                    month.commissionMinor != null &&
                    month.commissionMinor > 0
                      ? formatCivilDate(month.payableOn, locale)
                      : dash}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow
                sx={{
                  '& > *': {
                    fontWeight: 600,
                    color: 'text.primary',
                    fontSize: 'inherit',
                    borderTop: 1,
                    borderTopColor: 'divider',
                  },
                }}
              >
                <TableCell component="th" scope="row" sx={{ whiteSpace: 'nowrap' }}>
                  {t('commissionPlans.example.total')}
                </TableCell>
                <TableCell />
                <TableCell />
                <MoneyCell minor={totals.commissionMinor} currency={example.currency} />
                {example.hasBounty ? (
                  <MoneyCell minor={totals.bountyMinor} currency={example.currency} />
                ) : null}
                <MoneyCell minor={totals.marginBeforeMinor} currency={example.currency} />
                <MoneyCell minor={totals.marginAfterMinor} currency={example.currency} />
                <TableCell />
              </TableRow>
            </TableFooter>
          </Table>
        </Box>
        {totals.excludesMissing ? (
          <Typography variant="caption" component="p" sx={{ color: 'text.secondary', mt: 1 }}>
            {t('common.totalExcludesMissing')}
          </Typography>
        ) : null}
        {hasMilestones ? (
          <Typography variant="caption" component="p" sx={{ color: 'text.secondary', mt: 1.5 }}>
            {t('commissionPlans.example.noMilestones')}
          </Typography>
        ) : null}
      </BusyRegion>
    );
  }

  return (
    <Paper sx={{ p: 2.5 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1.5,
          mb: 1.5,
        }}
      >
        <Typography variant="subtitle1" component="h2">
          {title}
        </Typography>
        {choices.length > 0 && card ? (
          <TextField
            select
            size="small"
            label={t('commissionPlans.example.card')}
            value={card.id}
            onChange={(event) => onPickCard(Number(event.target.value))}
            sx={{ minWidth: FIELD_WIDTH.sm }}
          >
            {choices.map((choice) => (
              <MenuItem key={choice.id} value={choice.id}>
                {choice.name || t('rateCard.panel.unknown')}
              </MenuItem>
            ))}
          </TextField>
        ) : null}
      </Box>
      {body()}
    </Paper>
  );
}
